/**
 * The editor side of creating entities: puts a preset at a world point (the
 * camera's focus point unless one is given, e.g. where an asset was dropped),
 * under the level root unless a parent is given, and dispatches `createEdit`
 * as one undo step. Surfaces (the Outliner's Create menu, a row's Create empty
 * child, the Content Browser's assets) only choose the preset and the place.
 */
import { useCallback } from "react";
import type { CommandOrigin } from "../../commands/types";
import type { Vec3 } from "../../core/math/vec3";
import type { EntityId } from "../../world/entity";
import { levelRootOf } from "../../world/hierarchy";
import { runEdit, useEditor } from "../EditorContext";
import { focusPoint } from "../viewport/cameraMath";
import { createEdit, landing } from "./createModel";
import type { CreatePreset } from "./createModel";

/** Settings keep the grid snap in centimeters (Unreal units); positions are meters. */
const CM_PER_METER = 100;

export interface CreatePlace {
  /** The parent; the level root when omitted. */
  readonly parent?: EntityId;
  /** Where in the world; the camera's focus point when omitted. */
  readonly at?: Vec3;
}

/**
 * Where a new entity lands (`landing`): under `parent` (the level root by
 * default), for the world point `at` (the camera's focus point by default),
 * snapped to the translate grid when snapping is on.
 */
export function useLanding() {
  const { world, camera, bridge, settings } = useEditor();

  return useCallback(
    ({ parent, at }: CreatePlace = {}) => {
      const under = parent ?? levelRootOf(world.getSnapshot().entities);
      const point = at ?? focusPoint(camera.getSnapshot());
      const { snapEnabled, snapTranslate } = settings.getSnapshot();
      const grid = snapEnabled ? snapTranslate / CM_PER_METER : null;
      const parentWorld = under === null ? null : bridge.worldMatrixOf(under);
      return { parent: under, ...landing(point, parentWorld, grid) };
    },
    [world, camera, bridge, settings],
  );
}

export function useCreate(origin: CommandOrigin) {
  const { world, dispatch, dispatchAll } = useEditor();
  const land = useLanding();

  return useCallback(
    (preset: CreatePreset, place: CreatePlace = {}) => {
      const { parent, position } = land(place);
      const { entities } = world.getSnapshot();
      runEdit(
        { dispatch, dispatchAll },
        createEdit(preset, { entities, parent, position }, origin),
      );
    },
    [world, land, dispatch, dispatchAll, origin],
  );
}
